import { A, G } from "@mobily/ts-belt";
import { parseISO } from "date-fns";
import * as constants from "./constants";

export const isCents = (type) => type === constants.PROMO_CODE_TYPES.CENTS;

export const isPercentage = (type) =>
  type === constants.PROMO_CODE_TYPES.PERCENTAGE;

export const keyForType = (type) =>
  isPercentage(type) ? "endAdornment" : "startAdornment";

export const labelForType = (type) =>
  isPercentage(type) ? "Percentage" : "Amount";

export const helperTextForType = (type) =>
  isPercentage(type) ? "Max. 100%" : "Max. $300";

export const textForType = (type) => (isPercentage(type) ? "%" : "$");

export async function createPromoCode(promoCode) {
  const response = await fetch("/api/promoCode", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(promoCode),
  });

  const json = await response.json();

  if (!response.ok) {
    const { errors } = json;
    return Promise.reject(A.head(errors));
  }

  if (json?.promoCode) {
    return json?.promoCode;
  }
}

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
});

export function discountFor(promoCode) {
  if (isPercentage(promoCode.type)) {
    return `${promoCode.discountPercentage * 100}% off`;
  }
  return `${currencyFormatter.format(promoCode.discountCents / 100)} off`;
}

export function discountMaxCentsFor(promoCode) {
  if (
    promoCode.discountMaxCents === 0 ||
    promoCode.type === constants.PROMO_CODE_TYPES.CENTS
  ) {
    return "-";
  }

  return currencyFormatter.format(promoCode.discountMaxCents / 100);
}

export function minPurchaseCentsFor(promoCode) {
  if (promoCode.minPurchaseCents === 0) {
    return "-";
  }

  return currencyFormatter.format(promoCode.minPurchaseCents / 100);
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  year: "numeric",
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

export function formatDate(dateStr) {
  return dateFormatter.format(new Date(dateStr));
}

export function formatDateWithTimezone(dateStr) {
  return dateFormatter.format(new Date(dateStr));
}

export function maxUsesFor({ maxUses, uses }) {
  return G.isNumber(maxUses) ? `${uses} / ${maxUses}` : `${uses}`;
}

export function maxUsesPerUserFor({ maxUsesPerUser }) {
  return G.isNumber(maxUsesPerUser) ? maxUsesPerUser : "∞";
}

export function appliesToFor({ appliesTo }) {
  return appliesTo === constants.PROMO_CODE_APPLIES_TO.SUBTOTAL
    ? "Subtotal"
    : "Total";
}

export function minItemsFor({ minItems }) {
  return minItems === 0 ? "-" : String(minItems);
}

export function productTagFor({ productTagString }) {
  return productTagString || null;
}

export function statusFor(promoCode) {
  if (!promoCode.active) {
    return "Inactive";
  }

  const now = new Date();
  const expiry = parseISO(promoCode.expiryDate);

  if (expiry < now) {
    return "Expired";
  }

  const effective = parseISO(promoCode.effectiveDate);

  if (effective > now) {
    return "Scheduled";
  }

  return "Active";
}
